import { useLruCache } from "@/composables/useLruCache";

type CacheEntry = { url?: string; promise?: Promise<string> };

const MAX_CACHE_ENTRIES = 10000;

async function fetchAsObjectUrl(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status}`);
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

export function createCellImageCache(maxEntries = MAX_CACHE_ENTRIES) {
  const cache = useLruCache<CacheEntry>(maxEntries, (entry) => {
    if (entry.url) {
      URL.revokeObjectURL(entry.url);
    }
  });

  const getCachedUrl = async (url: string): Promise<string> => {
    const entry = cache.get(url);
    if (entry?.url) {
      return entry.url;
    }
    if (entry?.promise) {
      return entry.promise;
    }
    const promise = fetchAsObjectUrl(url)
      .then((objectUrl) => {
        const current = cache.peek(url);
        if (current?.promise !== promise) {
          return objectUrl;
        }
        cache.set(url, { url: objectUrl });
        return objectUrl;
      })
      .catch((error) => {
        const current = cache.peek(url);
        if (current?.promise === promise) {
          cache.delete(url);
        }
        throw error;
      });
    cache.set(url, { promise });
    return promise;
  };

  return { getCachedUrl };
}

const sharedCache = createCellImageCache();

export function useCellImageCache() {
  return sharedCache;
}
